import React, { useState, useMemo } from 'react';
import { Search, Flame, Rocket, AlertTriangle, ShieldAlert, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type { ScannerRecord, EggPhase, MigrationBucket } from '../types';

interface StockScannerProps {
  records: ScannerRecord[];
  selectedPhase: EggPhase | null;
  selectedSector?: string | null;
  onClearSector?: () => void;
  onClearPhase?: () => void;
  onSelectStock: (stock: ScannerRecord) => void;
}

export const StockScanner: React.FC<StockScannerProps> = ({
  records,
  selectedPhase,
  selectedSector = null,
  onClearSector,
  onClearPhase,
  onSelectStock,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [migrationFilter, setMigrationFilter] = useState<MigrationBucket | 'ALL'>('ALL');
  const [signalFilter, setSignalFilter] = useState<string>('ALL');
  const [pageSize, setPageSize] = useState<number>(25);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState<keyof ScannerRecord>('rs20');
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  // Migration counts across all records
  const migrationCounts = useMemo(() => {
    let cb = 0;
    let ib = 0;
    let oa = 0;
    let bw = 0;
    for (const r of records) {
      if (r.migration_bucket === 'CONTRARIAN_BUY') cb++;
      else if (r.migration_bucket === 'IGNITION_BREAKOUT') ib++;
      else if (r.migration_bucket === 'OVERHEATING_ALERT') oa++;
      else if (r.migration_bucket === 'BREAKDOWN_WARNING') bw++;
    }
    return { cb, ib, oa, bw };
  }, [records]);

  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      // Phase filter from interactive egg
      if (selectedPhase && r.phase !== selectedPhase) {
        return false;
      }
      // Sector filter from sector heatmap
      if (selectedSector && r.sector !== selectedSector) {
        return false;
      }
      // Migration bucket filter
      if (migrationFilter !== 'ALL' && r.migration_bucket !== migrationFilter) {
        return false;
      }
      // Signal filter (supports both baseline tokens and dynamic regime-specific compound signals)
      if (signalFilter !== 'ALL') {
        const sig = r.resonance_signal || '';
        const matchesSignal =
          sig === signalFilter ||
          (signalFilter === 'STRONG_BUY' && sig.includes('STRONG_BUY')) ||
          (signalFilter === 'BUY' && (sig.includes('BUY') || sig.includes('HOLD'))) ||
          (signalFilter === 'WATCHLIST' && (sig.includes('WATCH') || sig.includes('PROBE') || sig.includes('CAUTION'))) ||
          (signalFilter === 'TRIM' && (sig.includes('TRIM') || sig.includes('SCALE') || sig.includes('TAKE_PROFIT') || sig.includes('TIGHTEN'))) ||
          (signalFilter === 'EXIT' && (sig.includes('EXIT') || sig.includes('CASH') || sig.includes('AVOID'))) ||
          (signalFilter === 'AGGRESSIVE_SELL' && (sig.includes('SELL') || sig.includes('DISTRIBUTE') || sig.includes('EXIT')));

        if (!matchesSignal) {
          return false;
        }
      }
      // Search term
      if (searchTerm) {
        const query = searchTerm.toLowerCase();
        return (
          r.symbol.toLowerCase().includes(query) ||
          r.name.toLowerCase().includes(query) ||
          r.sector.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [records, selectedPhase, selectedSector, migrationFilter, signalFilter, searchTerm]);

  // Sort filtered records
  const sortedRecords = useMemo(() => {
    const list = [...filteredRecords];
    list.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortAsc ? aVal.localeCompare(bVal, 'zh-Hant') : bVal.localeCompare(aVal, 'zh-Hant');
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortAsc ? aVal - bVal : bVal - aVal;
      }
      return 0;
    });
    return list;
  }, [filteredRecords, sortField, sortAsc]);

  // Pagination
  const totalPages = pageSize === -1 ? 1 : Math.max(1, Math.ceil(sortedRecords.length / pageSize));
  const activePage = Math.min(currentPage, totalPages);

  const paginatedRecords = useMemo(() => {
    if (pageSize === -1) return sortedRecords;
    const start = (activePage - 1) * pageSize;
    return sortedRecords.slice(start, start + pageSize);
  }, [sortedRecords, activePage, pageSize]);

  const handleSort = (field: keyof ScannerRecord) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  return (
    <div className="stitch-card rounded-xl flex flex-col h-full overflow-hidden">
      {/* Section Header & Top Controls Toolbar */}
      <div className="p-3.5 flex flex-col gap-3 bg-[#0b0f17]/75">
        {/* Title & Subtitle Row */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#38bdf8] shadow-[0_0_8px_rgba(56,189,248,0.6)]"></span>
            <div className="flex flex-wrap items-baseline gap-2">
              <h3 className="text-xs md:text-sm font-semibold text-slate-100">
                個股雙層掃描即時信號
              </h3>
              <span className="text-[11px] font-mono text-sky-400/85">
                (Institutional Screen Preset Feeds)
              </span>
            </div>
          </div>
          <span className="text-[11px] font-mono text-slate-300 bg-[#131a26] px-2 py-0.5 rounded border border-[#1e293b]/70">
            符合條件: <strong className="text-sky-300">{filteredRecords.length}</strong> / {records.length} 檔
          </span>
        </div>

        {/* Search, Filter Dropdown & Fresh Migration Preset Feeds */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Search & Active Filter Chips */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] max-w-xs">
              <Search className="w-3.5 h-3.5 text-sky-400/80 absolute left-2.5 top-2.5" />
              <input
                data-testid="scanner-search-input"
                type="text"
                placeholder="搜尋代碼、名稱或產業..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-[#131a26] border border-[#1e293b]/80 rounded-md pl-8 pr-3 py-1.5 text-xs text-slate-100 placeholder-slate-400 focus:outline-none focus:border-sky-400 font-mono"
              />
            </div>

            {/* Resonance Signal Filter Dropdown */}
            <select
              data-testid="scanner-signal-select"
              value={signalFilter}
              onChange={(e) => {
                setSignalFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-[#131a26] border border-[#1e293b]/80 rounded-md px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-sky-400 font-mono cursor-pointer"
            >
              <option value="ALL">全部共振信號</option>
              <option value="STRONG_BUY">積極買進 (Strong Buy)</option>
              <option value="BUY">加碼/續抱 (Buy / Hold)</option>
              <option value="WATCHLIST">觀望/自選 (Watchlist)</option>
              <option value="TRIM">減碼 (Trim)</option>
              <option value="EXIT">出清持股 (Exit)</option>
              <option value="AGGRESSIVE_SELL">積極出清 (Aggressive Sell)</option>
            </select>

            {(selectedSector || selectedPhase) && (
              <div data-testid="scanner-active-filters" className="flex items-center space-x-1.5 text-xs font-mono">
                {selectedSector && (
                  <span className="flex items-center space-x-1 bg-sky-950/60 text-sky-300 border border-sky-500/40 px-2 py-0.5 rounded-md">
                    <span>類股: {selectedSector}</span>
                    {onClearSector && (
                      <button
                        type="button"
                        data-testid="clear-sector-filter"
                        onClick={onClearSector}
                        className="text-slate-300 hover:text-white ml-1 font-bold"
                        aria-label="清除類股篩選"
                      >
                        ×
                      </button>
                    )}
                  </span>
                )}
                {selectedPhase && (
                  <span className="flex items-center space-x-1 bg-sky-950/60 text-sky-300 border border-sky-500/40 px-2 py-0.5 rounded-md">
                    <span>階段: {selectedPhase}</span>
                    {onClearPhase && (
                      <button
                        type="button"
                        data-testid="clear-phase-filter"
                        onClick={onClearPhase}
                        className="text-slate-300 hover:text-white ml-1 font-bold"
                        aria-label="清除階段篩選"
                      >
                        ×
                      </button>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Fresh Migration Preset Feeds matching Stitch */}
          <div className="flex items-center gap-1.5 overflow-x-auto text-[11px] font-mono">
            <button
              type="button"
              onClick={() => { setMigrationFilter('ALL'); setCurrentPage(1); }}
              className={`px-2.5 py-1 rounded-md transition-all border ${
                migrationFilter === 'ALL'
                  ? 'bg-primary-container text-white border-blue-400/50 font-semibold'
                  : 'bg-[#131a26] text-slate-300 border-[#1e293b]/70 hover:text-white'
              }`}
            >
              全部標的 ({records.length})
            </button>

            <button
              type="button"
              onClick={() => { setMigrationFilter('CONTRARIAN_BUY'); setCurrentPage(1); }}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all border ${
                migrationFilter === 'CONTRARIAN_BUY'
                  ? 'bg-blue-950/80 text-sky-300 border-sky-500/60 font-semibold'
                  : 'bg-[#131a26] text-slate-300 border-[#1e293b]/70 hover:text-sky-300'
              }`}
            >
              <Flame className="w-3 h-3 text-sky-400" />
              <span>逆向逢低買進 (B3→A1: {migrationCounts.cb}檔)</span>
            </button>

            <button
              type="button"
              onClick={() => { setMigrationFilter('IGNITION_BREAKOUT'); setCurrentPage(1); }}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all border ${
                migrationFilter === 'IGNITION_BREAKOUT'
                  ? 'bg-blue-950/80 text-sky-300 border-sky-500/60 font-semibold'
                  : 'bg-[#131a26] text-slate-300 border-[#1e293b]/70 hover:text-sky-300'
              }`}
            >
              <Rocket className="w-3 h-3 text-sky-400" />
              <span>點火突破 (A1→A2: {migrationCounts.ib}檔)</span>
            </button>

            <button
              type="button"
              onClick={() => { setMigrationFilter('OVERHEATING_ALERT'); setCurrentPage(1); }}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all border ${
                migrationFilter === 'OVERHEATING_ALERT'
                  ? 'bg-amber-950/60 text-amber-300 border-amber-500/50 font-semibold'
                  : 'bg-[#131a26] text-slate-300 border-[#1e293b]/70 hover:text-amber-300'
              }`}
            >
              <AlertTriangle className="w-3 h-3 text-amber-400" />
              <span>過熱警戒 (A2→A3: {migrationCounts.oa}檔)</span>
            </button>

            <button
              type="button"
              onClick={() => { setMigrationFilter('BREAKDOWN_WARNING'); setCurrentPage(1); }}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all border ${
                migrationFilter === 'BREAKDOWN_WARNING'
                  ? 'bg-rose-950/60 text-rose-300 border-rose-500/50 font-semibold'
                  : 'bg-[#131a26] text-slate-300 border-[#1e293b]/70 hover:text-rose-300'
              }`}
            >
              <ShieldAlert className="w-3 h-3 text-rose-400" />
              <span>破位停損 (A3→B1: {migrationCounts.bw}檔)</span>
            </button>
          </div>
        </div>
      </div>

      {/* High-Density Stock Matrix Table matching Stitch */}
      <div className="overflow-x-auto overflow-y-auto flex-1 min-h-[300px]">
        <table data-testid="scanner-table" className="w-full text-left border-collapse text-xs font-mono">
          <thead>
            <tr className="bg-[#0b0f17] text-slate-200 border-b border-[#182232] sticky top-0 z-10 h-9">
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('symbol')}>
                <span>標的代碼/名稱</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(Ticker)</span>{' '}
                {sortField === 'symbol' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('sector')}>
                <span>產業類別</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(Sector)</span>{' '}
                {sortField === 'sector' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('close')}>
                <span>現價</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(TWD)</span>{' '}
                {sortField === 'close' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('phase')}>
                <span>循環階段</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(Regime)</span>{' '}
                {sortField === 'phase' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('dog_ma200_bias')}>
                <span>200日乖離</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(Bias)</span>{' '}
                {sortField === 'dog_ma200_bias' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('vr60')}>
                <span>60日成交量比</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(VR60)</span>{' '}
                {sortField === 'vr60' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold cursor-pointer hover:text-white" onClick={() => handleSort('rs20')}>
                <span>相對強弱</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(RS)</span>{' '}
                {sortField === 'rs20' && (sortAsc ? '↑' : '↓')}
              </th>
              <th className="px-3 py-1.5 font-semibold text-right cursor-pointer hover:text-white" onClick={() => handleSort('ladder_position_pct')}>
                <span>估值天梯</span>{' '}
                <span className="text-[10px] text-sky-400/80 font-normal">(Ladder)</span>{' '}
                {sortField === 'ladder_position_pct' && (sortAsc ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#182232]/70 text-slate-200">
            {paginatedRecords.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-slate-500">
                  查無符合條件的個股標的
                </td>
              </tr>
            ) : (
              paginatedRecords.map((stock) => {
                const cleanCode = stock.symbol.replace(/\.(TW|TWO)$/, '');
                const bias = (stock.dog_ma200_bias || 0) * 100;
                const vr60 = stock.vr60 || 1.0;
                const vrPct = Math.round(vr60 * 100);
                const isLeader = (stock.rs20 || 0) >= 1.1;

                let phaseBadge = 'bg-surface-container text-slate-300 border-border-subtle';
                if (stock.phase === 'A2') phaseBadge = 'bg-blue-950/60 text-terminal-cyan border-blue-500/40';
                else if (stock.phase === 'A1') phaseBadge = 'bg-blue-950/40 text-blue-300 border-blue-600/30';
                else if (stock.phase === 'A3') phaseBadge = 'bg-amber-950/60 text-amber-400 border-amber-500/40';
                else if (stock.phase === 'B1') phaseBadge = 'bg-rose-950/60 text-rose-400 border-rose-500/40';
                else if (stock.phase === 'B3') phaseBadge = 'bg-blue-950/80 text-primary border-primary/40';

                return (
                  <tr
                    key={stock.symbol}
                    data-testid={`scanner-row-${cleanCode}`}
                    onClick={() => onSelectStock(stock)}
                    className="hover:bg-slate-800/40 cursor-pointer transition-colors h-9"
                  >
                    {/* Ticker / Name */}
                    <td className="px-3 py-1">
                      <div className="flex items-center gap-1.5 font-bold">
                        <span className="text-terminal-cyan">{cleanCode}</span>
                        <span className="text-slate-100 font-normal">{stock.name}</span>
                        {stock.is_disposition && (
                          <span className="px-1 py-0.2 bg-rose-950/80 text-rose-300 border border-rose-500/40 text-[9px] rounded">
                            處置
                          </span>
                        )}
                        {stock.is_limit_locked && (
                          <span className="px-1 py-0.2 bg-amber-950/80 text-amber-300 border border-amber-500/40 text-[9px] rounded">
                            鎖漲跌
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Sector */}
                    <td className="px-3 py-1 text-slate-400 text-[11px]">
                      {stock.sector}
                    </td>

                    {/* Price */}
                    <td className="px-3 py-1 font-bold text-market-up">
                      {stock.close >= 1000 ? stock.close.toLocaleString('en-US', { minimumFractionDigits: 1 }) : stock.close.toFixed(1)}
                    </td>

                    {/* Phase Badge */}
                    <td className="px-3 py-1">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${phaseBadge}`}>
                        {stock.phase} {stock.migration_bucket === 'IGNITION_BREAKOUT' ? '突破' : ''}
                      </span>
                    </td>

                    {/* Bias */}
                    <td className={`px-3 py-1 ${bias >= 0 ? 'text-terminal-cyan' : 'text-slate-400'}`}>
                      {bias >= 0 ? '+' : ''}{bias.toFixed(1)}%
                    </td>

                    {/* VR60 */}
                    <td className="px-3 py-1 text-slate-300">
                      {vrPct}% {vr60 > 1.3 ? '(量增)' : vr60 < 0.8 ? '(量縮)' : '(常態)'}
                    </td>

                    {/* RS */}
                    <td className="px-3 py-1 text-slate-100 font-semibold">
                      {(stock.rs20 || 1.0).toFixed(2)} {isLeader && <span className="text-terminal-cyan">★</span>}
                    </td>

                    {/* Ladder */}
                    <td className="px-3 py-1 text-right text-slate-300 text-[11px]">
                      {stock.ladder_position_pct !== null ? `${Math.round(stock.ladder_position_pct * 100)}%` : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs font-mono bg-[#0b0f17]/80 text-slate-300">
        <div className="flex items-center gap-2">
          <span>
            顯示 <strong className="text-sky-300">{sortedRecords.length}</strong> 檔中的 {pageSize === -1 ? '全部' : `${Math.min((activePage - 1) * pageSize + 1, sortedRecords.length)} - ${Math.min(activePage * pageSize, sortedRecords.length)}`} 檔
          </span>

          <span className="text-slate-600">|</span>

          <label className="flex items-center gap-1">
            <span className="text-slate-400">每頁:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="bg-[#131a26] border border-[#1e293b]/80 rounded px-1.5 py-0.5 text-slate-200 focus:outline-none cursor-pointer"
            >
              <option value={25}>25 檔</option>
              <option value={50}>50 檔</option>
              <option value={100}>100 檔</option>
              <option value={200}>200 檔</option>
              <option value={-1}>全部 ({sortedRecords.length})</option>
            </select>
          </label>
        </div>

        {pageSize !== -1 && totalPages > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={activePage === 1}
              onClick={() => setCurrentPage(1)}
              className="p-1 rounded bg-[#131a26] border border-[#1e293b]/80 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#192231] text-slate-200"
              title="第一頁"
            >
              <ChevronsLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              disabled={activePage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="p-1 rounded bg-[#131a26] border border-[#1e293b]/80 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#192231] text-slate-200"
              title="上一頁"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>

            <span className="px-2 text-slate-200">
              第 <strong className="text-sky-300">{activePage}</strong> / {totalPages} 頁
            </span>

            <button
              type="button"
              disabled={activePage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="p-1 rounded bg-[#131a26] border border-[#1e293b]/80 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#192231] text-slate-200"
              title="下一頁"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              disabled={activePage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
              className="p-1 rounded bg-[#131a26] border border-[#1e293b]/80 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#192231] text-slate-200"
              title="最後一頁"
            >
              <ChevronsRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
